// `?bake=1` — `window.__READ_PIXEL__(fx, fy)`, a pixel out of the rendered frame.
//
// The mechanism and everything load-bearing about it live in
// `src/lib/canvasReadback.js`; this is the mount that publishes it. Only under
// `?bake=1`, so a normal session allocates nothing and renders nothing extra.
//
// It exists because reading a canvas needs `preserveDrawingBuffer`, which three's
// node renderer has on neither backend — so the in-page `gl.readPixels` this
// replaces returned BLACK, or threw on a null WebGL context. A pixel assertion
// does not fail with an error in that case, it fails with the wrong COLOUR:
// `backdrop-pixels` read black where it expected the authored sky, which presents
// as "the backdrop is broken" rather than "the readback is".

import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { readPixel, readsTopDown, disposeReadback } from '../lib/canvasReadback.js'

export default function PixelReadback() {
  const gl = useThree((s) => s.gl)
  const scene = useThree((s) => s.scene)
  const camera = useThree((s) => s.camera)

  useEffect(() => {
    if (!gl || !scene || !camera) return

    window.__READ_PIXEL__ = (fx, fy) => readPixel(gl, scene, camera, fx, fy)
    // What built it, so a caller that gets an unexpected colour can tell a
    // renderer difference from a scene one without guessing.
    window.__READ_PIXEL_INFO__ = {
      renderer: gl.constructor?.name ?? null,
      backend: readsTopDown(gl) ? 'webgpu' : 'webgl-fallback',
    }
    console.info(`[bake] __READ_PIXEL__ ready on ${window.__READ_PIXEL_INFO__.renderer}`
      + ` (${window.__READ_PIXEL_INFO__.backend})`)

    return () => {
      delete window.__READ_PIXEL__
      delete window.__READ_PIXEL_INFO__
      disposeReadback(gl)
    }
  }, [gl, scene, camera])

  return null
}
